import { FC } from 'react';
import { DigitalFormScopeHooks } from '@frontend/digital-forms-scope';
import { useTranslation } from '@frontend/i18n';
import { Modal, ContentLoader } from '@frontend/design-system';
import { pendoTags } from '../../../../../../../../../../../../shared/constants';
import {
  useFormsSubmissionsV2Context,
  useFormsSubmissionsDetailsV2Context,
} from '../../../../../../../../../../provider';
import { useWritebackStore } from '../../../../../provider';
import { PROVIDER_REVIEW_STEP } from '../../../../../writeback.types';
import { useWritebackRetry } from '../../../../use-writeback-retry';
import { getProviderUser } from '../provider-review-flow.utils';
import { useApproveSubmission } from '../use-approve-submission';
import { useNavigateProviderReviewSteps } from '../use-navigate-provider-review-steps';
import { useRejectSubmission } from '../use-reject-submission';
import { useShouldSyncSubmission } from '../use-should-sync-submission';
import { getPendoTagForProviderReviewStep, shouldDisablePrimaryButton } from './provider-review-footer.utils';

const STEPS_WITH_BACK_BUTTON: Set<PROVIDER_REVIEW_STEP> = new Set([
  PROVIDER_REVIEW_STEP.APPROVAL_DISCLAIMER,
  PROVIDER_REVIEW_STEP.APPROVAL_SIGNATURE,
  PROVIDER_REVIEW_STEP.APPROVAL_NOTE,
  PROVIDER_REVIEW_STEP.REJECTION_NOTE,
]);

interface FooterContentProps {
  onCloseModal: () => void;
}

const FooterContent: FC<FooterContentProps> = ({ onCloseModal }) => {
  const { t } = useTranslation('forms');
  const { locationId = '', submissionId = '' } = useFormsSubmissionsV2Context();
  const { submissionDetails = {} } = useFormsSubmissionsDetailsV2Context();
  const { hasPermissionToReviewSubmissions } = DigitalFormScopeHooks.useFormsACL({ locationId });
  const { goBackAStep, goToNextStep } = useNavigateProviderReviewSteps();
  const { shouldAutoSync, needsManualSyncing } = useShouldSyncSubmission();
  const { triggerWritebackRetry } = useWritebackRetry();
  const { approveSubmission } = useApproveSubmission();
  const { rejectSubmission } = useRejectSubmission();
  const {
    currentProviderReviewStep,
    providerReviewNote,
    setSaveSignatureTrigger,
    savedSignature,
    isUpdateSignatureActive,
    signature,
    setIsSavingProviderReview,
    saveSignatureTrigger,
    isSavingProviderReview,
  } = useWritebackStore([
    'currentProviderReviewStep',
    'providerReviewNote',
    'setSaveSignatureTrigger',
    'savedSignature',
    'isUpdateSignatureActive',
    'signature',
    'setIsSavingProviderReview',
    'saveSignatureTrigger',
    'isSavingProviderReview',
  ]);
  const { providerName } = submissionDetails;
  const shouldShowBackButton = STEPS_WITH_BACK_BUTTON.has(currentProviderReviewStep);
  const _shouldDisablePrimaryButton = shouldDisablePrimaryButton({ currentProviderReviewStep, providerReviewNote });
  const primaryLabel = ((): string => {
    switch (currentProviderReviewStep) {
      case PROVIDER_REVIEW_STEP.APPROVAL_NOTE: {
        if (shouldAutoSync) {
          return t('Approve & Sync');
        }

        return t('Approve');
      }

      case PROVIDER_REVIEW_STEP.REJECTION_NOTE:
        return t('Reject');

      case PROVIDER_REVIEW_STEP.APPROVAL_DISCLAIMER:
        return t('Continue');

      case PROVIDER_REVIEW_STEP.APPROVAL_SIGNATURE:
        return t('Continue to Add Notes');

      default:
        return t('Next');
    }
  })();

  async function approveThisSubmission(skipRefetch = false) {
    const userName = hasPermissionToReviewSubmissions ? providerName : '';
    const user = getProviderUser();
    if (!signature || !user.email || !user.name) {
      return Promise.resolve({ success: false });
    }

    return await approveSubmission({
      locationId,
      note: providerReviewNote,
      submissionId,
      signature,
      user: {
        ...user,
        ...(userName && { name: userName }),
      },
      skipRefetch,
    });
  }

  async function rejectThisSubmission() {
    const user = getProviderUser();
    if (!user.email || !user.name) {
      return Promise.reject();
    }

    return await rejectSubmission({
      locationId,
      note: providerReviewNote,
      submissionId,
      user,
    });
  }

  async function approveAndSyncSubmission() {
    setIsSavingProviderReview(true);
    await approveThisSubmission(true);
    await triggerWritebackRetry();
    setIsSavingProviderReview(false);
  }

  async function primaryClickHandler() {
    switch (currentProviderReviewStep) {
      case PROVIDER_REVIEW_STEP.APPROVAL_SIGNATURE: {
        if (!(savedSignature && !isUpdateSignatureActive)) {
          // This is to start uploading the signature before moving to the next step
          setSaveSignatureTrigger(true);
          return;
        }

        goToNextStep();
        break;
      }

      case PROVIDER_REVIEW_STEP.APPROVAL_NOTE: {
        if (shouldAutoSync) {
          await approveAndSyncSubmission();
          onCloseModal();
          return;
        }

        if (needsManualSyncing) {
          goToNextStep();
          return;
        }

        setIsSavingProviderReview(true);
        await approveThisSubmission();
        setIsSavingProviderReview(false);
        onCloseModal();
        break;
      }

      case PROVIDER_REVIEW_STEP.SYNC:
        await approveAndSyncSubmission();
        onCloseModal();
        break;

      case PROVIDER_REVIEW_STEP.REJECTION_NOTE:
        setIsSavingProviderReview(true);
        await rejectThisSubmission();
        setIsSavingProviderReview(false);
        onCloseModal();
        break;

      default:
        goToNextStep();
    }
  }

  return (
    <>
      <Modal.Footer
        primary={{
          label: primaryLabel,
          onClick: primaryClickHandler,
          disabled: _shouldDisablePrimaryButton,
          trackingId: getPendoTagForProviderReviewStep(currentProviderReviewStep),
        }}
        secondary={{
          label: t('Cancel'),
          onClick: onCloseModal,
          trackingId: pendoTags.providerReviewV2.closeModal,
        }}
        tertiary={{
          label: shouldShowBackButton ? t('Back') : undefined,
          onClick: shouldShowBackButton ? goBackAStep : undefined,
          trackingId: pendoTags.providerReviewV2.goBackAStep,
        }}
      />
      <ContentLoader
        show={saveSignatureTrigger || isSavingProviderReview}
        message={isSavingProviderReview ? t('Saving the review...') : t('Saving the signature...')}
      />
    </>
  );
};

interface ProviderReviewFlowFooterProps extends FooterContentProps {
  cannotUseProviderReviewFlow: boolean;
}

const ProviderReviewFlowFooter: FC<ProviderReviewFlowFooterProps> = ({
  cannotUseProviderReviewFlow,
  ...otherProps
}) => {
  const { currentProviderReviewStep } = useWritebackStore(['currentProviderReviewStep']);

  if (cannotUseProviderReviewFlow || currentProviderReviewStep === PROVIDER_REVIEW_STEP.APPROVE_OR_REJECT) {
    return null;
  }

  return <FooterContent {...otherProps} />;
};

export default ProviderReviewFlowFooter;
